import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpEvent, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ContractNegotiation, ContractNegotiationRequest, ContractNegotiationState, IdResponse, QuerySpec } from '../model';
import { ManagementRouteService } from '../common/managementRoute.service';
import { MANAGEMENT_API_CONTEXT } from '../variables';

@Injectable({
  providedIn: 'root'
})
export class ContractNegotiationService {

  constructor(
    private readonly http: HttpClient,
    private readonly managementRoute: ManagementRouteService
  ) {}

  public cancelNegotiation(id: string, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<any>;
  public cancelNegotiation(id: string, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpResponse<any>>;
  public cancelNegotiation(id: string, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpEvent<any>>;
  public cancelNegotiation(id: string): Observable<any> {
    return this.terminate(id, 'Cancelled by DataDashboard');
  }

  public declineNegotiation(id: string, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<any>;
  public declineNegotiation(id: string, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpResponse<any>>;
  public declineNegotiation(id: string, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpEvent<any>>;
  public declineNegotiation(id: string): Observable<any> {
    return this.terminate(id, 'Terminated by the DataDashboard');
  }

  public getNegotiation(id: string, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<ContractNegotiation>;
  public getNegotiation(id: string, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpResponse<ContractNegotiation>>;
  public getNegotiation(id: string, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpEvent<ContractNegotiation>>;
  public getNegotiation(id: string): Observable<any> {
    const url = this.managementRoute.getManagementBaseUrl();
    return this.http.get<ContractNegotiation>(`${url}/contractnegotiations/${encodeURIComponent(id)}`);
  }

  public getNegotiationState(id: string, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<ContractNegotiationState>;
  public getNegotiationState(id: string, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpResponse<ContractNegotiationState>>;
  public getNegotiationState(id: string, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpEvent<ContractNegotiationState>>;
  public getNegotiationState(id: string): Observable<any> {
    const url = this.managementRoute.getManagementBaseUrl();
    return this.http.get<ContractNegotiationState>(`${url}/contractnegotiations/${encodeURIComponent(id)}/state`);
  }

  public initiateContractNegotiation(negotiationInitiateRequest: ContractNegotiationRequest, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<IdResponse>;
  public initiateContractNegotiation(negotiationInitiateRequest: ContractNegotiationRequest, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpResponse<IdResponse>>;
  public initiateContractNegotiation(negotiationInitiateRequest: ContractNegotiationRequest, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpEvent<IdResponse>>;
  public initiateContractNegotiation(negotiationInitiateRequest: ContractNegotiationRequest): Observable<any> {
    const url = this.managementRoute.getManagementBaseUrl();
    const body = this.toJsonLdContractRequest(negotiationInitiateRequest);
    return this.http.post<IdResponse>(`${url}/contractnegotiations`, body);
  }

  public queryNegotiations(querySpec: QuerySpec, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<Array<ContractNegotiation>>;
  public queryNegotiations(querySpec: QuerySpec, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpResponse<Array<ContractNegotiation>>>;
  public queryNegotiations(querySpec: QuerySpec, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpEvent<Array<ContractNegotiation>>>;
  public queryNegotiations(querySpec: QuerySpec): Observable<any> {
    const url = this.managementRoute.getManagementBaseUrl();
    return this.http.post<ContractNegotiation[]>(`${url}/contractnegotiations/request`, this.toJsonLdQuerySpec(querySpec));
  }

  private terminate(id: string, reason: string): Observable<any> {
    const url = this.managementRoute.getManagementBaseUrl();
    const body = {
      '@context': [MANAGEMENT_API_CONTEXT],
      '@type': 'TerminateNegotiation',
      reason,
    };
    return this.http.post(`${url}/contractnegotiations/${encodeURIComponent(id)}/terminate`, body);
  }

  private toJsonLdContractRequest(req: ContractNegotiationRequest): Record<string, any> {
    const { counterPartyAddress, policy, ...rest } = req as any;
    const body: Record<string, any> = {
      '@context': [MANAGEMENT_API_CONTEXT],
      '@type': 'ContractRequest',
      counterPartyAddress,
      policy,
    };
    // Pass through whichever field was provided, without remapping
    if (rest['protocol'] !== undefined) body['protocol'] = rest['protocol'];
    if (rest['profile']  !== undefined) body['profile']  = rest['profile'];
    return body;
  }

  private toJsonLdQuerySpec(querySpec?: QuerySpec): Record<string, any> {
    const body: Record<string, any> = {
      '@context': [MANAGEMENT_API_CONTEXT],
      '@type': 'QuerySpec',
      offset: querySpec?.offset ?? 0,
      limit: querySpec?.limit ?? 50,
    };
    if (querySpec?.sortOrder) body['sortOrder'] = querySpec.sortOrder;
    if (querySpec?.sortField) body['sortField'] = querySpec.sortField;
    if (querySpec?.filterExpression?.length) body['filterExpression'] = querySpec.filterExpression;
    return body;
  }
}
